import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GroupNodeContent,
  GroupNodeHeader,
  GroupNodeHeaderAction,
  GroupNodeHeaderActions,
  GroupNodeHeaderIcon,
  GroupNodeHeaderLeft,
  GroupNodeHeaderName,
  GroupNodeWrapper,
} from '../../components/Tree/Composable';
import {
  LoadingOutlined,
  MinusCircleFilled,
  PlusCircleFilled,
} from '@ant-design/icons';
import { Button, Checkbox, Spin } from 'antd';
import { ROOT_GROUP_ID } from '../../constants/group';
import {
  getCameraGroupsByParentId,
  useGetCameraGroupsByParentId,
} from '../../hooks/socket/cameras';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAdvanceSearchV2 } from './use-search';
import { extractPath } from '../../untils/advance-search';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { getRecoveryGroupCameraTabOfSharePage } from '../../redux/recovery/reducer';
const { Search } = Input;

const GroupNode = ({
  group,
  getChildren,
  getIsSelected,
  isParentSelected,
  onSelect,
  getIsExpanded,
  onExpand,
  level,
}) => {
  const [children, setChildren] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const isExpanded = getIsExpanded(group.id);
  const isSelected = isParentSelected || getIsSelected(group.id);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    setIsLoading(true);
    getChildren(group.id)
      .then(setChildren)
      .finally(() => setIsLoading(false));
  }, [group.id, getChildren, isExpanded]);

  const renderChildren = () => {
    if (!isExpanded) {
      return null;
    }

    return (
      <GroupNodeContent>
        {children?.map((child) => (
          <GroupNode
            key={child.id}
            group={child}
            getChildren={getChildren}
            getIsSelected={getIsSelected}
            isParentSelected={isSelected}
            getIsExpanded={getIsExpanded}
            onSelect={onSelect}
            onExpand={onExpand}
            level={level + 1}
          />
        ))}
      </GroupNodeContent>
    );
  };

  return (
    <GroupNodeWrapper level={level} isExpanded={isExpanded}>
      <GroupNodeHeader>
        <GroupNodeHeaderLeft>
          <GroupNodeHeaderIcon
            onClick={() => {
              onExpand(group.id);
            }}
          >
            {isExpanded ? <MinusCircleFilled /> : <PlusCircleFilled />}
          </GroupNodeHeaderIcon>

          <GroupNodeHeaderName>{group.name}</GroupNodeHeaderName>
        </GroupNodeHeaderLeft>
        <GroupNodeHeaderActions>
          {isLoading && (
            <GroupNodeHeaderAction>
              <Spin size="small" indicator={<LoadingOutlined spin />} />
            </GroupNodeHeaderAction>
          )}
          <GroupNodeHeaderAction>
            <Checkbox
              checked={isSelected}
              onChange={() => onSelect(group.id)}
            />
          </GroupNodeHeaderAction>
        </GroupNodeHeaderActions>
      </GroupNodeHeader>
      {renderChildren()}
    </GroupNodeWrapper>
  );
};

const buildTree = (records) => {
  const { TbDeviceGroup = [] } = records;
  const isRootGroup = {};
  const groups = {};

  // Prepare node map
  TbDeviceGroup.forEach((device) => {
    Object.entries(device.deviceGroups).forEach(([groupId, group]) => {
      const treeNode = {
        group,
        subGroups: [],
      };

      groups[groupId] = treeNode;
    });
  });

  // Build tree
  TbDeviceGroup.forEach((device) => {
    const path = extractPath(device.deviceGroupPath);

    const rootGroupId = path[0];

    isRootGroup[rootGroupId] = true;

    for (let i = 1; i < path.length; i++) {
      const currentGroupId = path[i];
      const parentGroupId = path[i - 1];

      if (
        !groups[parentGroupId].subGroups.find(
          (group) => group.group.id === Number(currentGroupId),
        )
      ) {
        groups[parentGroupId].subGroups.push(groups[currentGroupId]);
        groups[parentGroupId].subGroups.sort((a, b) =>
          a.group.name.localeCompare(b.group.name),
        );
      }
    }
  });

  console.log('buildTree', records);

  return {
    rootGroups: Object.values(groups).filter(
      (group) => isRootGroup[group.group.id],
    ),
    allGroups: groups,
  };
};

/**
 * Data structure:
 *  Group map: GroupId -> Group // lookup by id
 *  Tree map: GroupId -> [groupId,...] // relationship in flat object
 *  Get children:
 *   (1)If: treemap has children:
 *     GroupId -> children -> GroupMap[childrenId][]
 *   Else:
 *     fetch children -> assign to group map, assign to treemap -> go to 1
 */

export const GroupTree = ({ namespace, onSelect, onSearch }) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const groupMap = useRef({});
  const treeMap = useRef({});
  const [expandedMap, setExpandedMap] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const { data, isLoading } = useGetCameraGroupsByParentId({
    namespace,
    parentDeviceGroupId: ROOT_GROUP_ID,
  });

  const { selectedGroup: recoverySelectedGroupCamera } =
    useSelector(getRecoveryGroupCameraTabOfSharePage) || {};

  useEffect(() => {
    if (data?.deviceGroups) {
      data.deviceGroups.forEach((group) => {
        groupMap.current[group.id] = group;
      });
    }
  }, [data]);

  const getChildren = useCallback(
    (groupId) => {
      if (treeMap.current[groupId]) {
        return Promise.resolve(
          treeMap.current[groupId].map((id) => groupMap.current[id]),
        );
      }

      return getCameraGroupsByParentId({
        namespace,
        parentDeviceGroupId: groupId,
      }).then((subgroupData) => {
        treeMap.current[groupId] = subgroupData.deviceGroups.map(
          (child) => child.id,
        );

        subgroupData.deviceGroups.forEach((child) => {
          groupMap.current[child.id] = child;
        });

        return treeMap.current[groupId].map((id) => groupMap.current[id]);
      });
    },
    [namespace],
  );

  const rootGroup = (data?.deviceGroups || []).filter(
    (group) => group.parentDeviceGroupId === -1,
  );

  const isExpanded = (groupId) => expandedMap[groupId] ?? false;
  const isSelected = (groupId) => selectedId === groupId;

  const onExpand = (groupId) => {
    setExpandedMap((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const onNodeSelect = (groupId) => {
    if (selectedId === groupId) {
      setSelectedId(null);
      onSelect([], []);
    } else {
      setSelectedId(groupId);
      onSelect([groupId], [groupMap.current[groupId]]);
    }
  };

  useEffect(() => {
    if (recoverySelectedGroupCamera) {
      const { keys } = recoverySelectedGroupCamera;
      setSelectedId(keys?.[0] ?? null);
    }
  }, [recoverySelectedGroupCamera]);

  return (
    <div className="group-tree-container">
      <SearchTree
        onSearchActiveChange={setIsSearchActive}
        namespace={namespace}
        onSelect={onNodeSelect}
        getIsSelected={isSelected}
        isSearchActive={isSearchActive}
        onSearch={onSearch}
      />

      {isLoading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Spin indicator={<LoadingOutlined spin />} />
        </div>
      )}

      {!isSearchActive && (
        <div
          className="group-tree"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            marginTop: '4px',
          }}
        >
          {rootGroup?.map((group) => (
            <GroupNode
              key={group.id}
              group={group}
              getChildren={getChildren}
              getIsSelected={isSelected}
              isParentSelected={false}
              getIsExpanded={isExpanded}
              onSelect={onNodeSelect}
              onExpand={onExpand}
              level={0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SearchTree = ({
  onSearchActiveChange,
  namespace,
  onSelect,
  getIsSelected,
  isSearchActive,
  onSearch,
}) => {
  const [isDebounce, setIsDebounce] = useState(false);
  const { t } = useTranslation();
  const {
    canLoadMore,
    search,
    loadMore,
    data,
    isLoading: isSearchLoading,
  } = useAdvanceSearchV2({ namespace });

  const isLoading = isDebounce || isSearchLoading;
  const [searchKeyword, setSearchKeyword] = useState('');
  const { searchGroupCamera: recoverySearchGroupCamera } =
    useSelector(getRecoveryGroupCameraTabOfSharePage) || {};

  const debounceSearch = useMemo(
    () =>
      debounce(
        (value) => {
          setIsDebounce(false);
          search(value);
        },
        2000,
        { leading: false, trailing: true },
      ),
    [search],
  );

  const onChange = useCallback(
    (e) => {
      const value = e?.target?.value ?? '';

      onSearch(value);
      setSearchKeyword(value);

      if (!value.length) {
        setIsDebounce(false);
        onSearchActiveChange(false);
      } else {
        onSearchActiveChange(true);
        setIsDebounce(true);
        debounceSearch(value);
      }
    },
    [debounceSearch, onSearch, onSearchActiveChange],
  );

  const result = useMemo(() => {
    return data?.records ? buildTree(data.records) : undefined;
  }, [data]);

  console.log('SEARCH RESULT', data);

  useEffect(() => {
    if (recoverySearchGroupCamera !== searchKeyword) {
      setSearchKeyword(recoverySearchGroupCamera);
      onChange({ target: { value: recoverySearchGroupCamera } });
    }
  }, [onChange, recoverySearchGroupCamera, searchKeyword]);

  return (
    <div className="search-camera" style={{ marginBottom: '8px' }}>
      <Search
        placeholder={t('Search group camera')}
        onChange={onChange}
        allowClear
        loading={isLoading}
        value={searchKeyword}
      />

      {result && isSearchActive && (
        <div
          className="group-tree"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            marginTop: '4px',
          }}
        >
          {result.rootGroups.map((group) => (
            <BasicSearchTree
              key={group.group.id}
              namespace={namespace}
              onSelect={onSelect}
              getIsSelected={getIsSelected}
              group={group}
              level={0}
              isParentSelected={false}
            />
          ))}

          {canLoadMore && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                loading={isLoading}
                onClick={loadMore}
                disabled={isLoading}
              >
                More results
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BasicSearchTree = ({
  namespace,
  onSelect,
  getIsSelected,
  group,
  level,
  isParentSelected,
}) => {
  const isSelected = isParentSelected || getIsSelected(group.group.id);

  return (
    <GroupNodeWrapper level={level}>
      <GroupNodeHeader>
        <GroupNodeHeaderLeft>
          <GroupNodeHeaderIcon>
            <MinusCircleFilled />
          </GroupNodeHeaderIcon>

          <GroupNodeHeaderName>{group.group.name}</GroupNodeHeaderName>
        </GroupNodeHeaderLeft>
        <GroupNodeHeaderActions>
          <GroupNodeHeaderAction>
            <Checkbox
              checked={isSelected}
              onChange={() => onSelect(group.group.id, group.group)}
            />
          </GroupNodeHeaderAction>
        </GroupNodeHeaderActions>
      </GroupNodeHeader>
      {group.subGroups.map((subGroup) => (
        <BasicSearchTree
          key={subGroup.group.id}
          namespace={namespace}
          onSelect={onSelect}
          getIsSelected={getIsSelected}
          group={subGroup}
          level={level + 1}
          isParentSelected={isSelected}
        />
      ))}
    </GroupNodeWrapper>
  );
};
